import domain from "../Domain";
import { AuthContext } from "../context/AuthContext";
import {ServerResponse} from "../types/ServerResponse";
import React from "react";

export default class NetworkUtil extends React.Component {
    static contextType = AuthContext;
    static UserContext;
    
    constructor(props) {
        super(props);
    }

    componentDidMount() {
        NetworkUtil.UserContext = this.context;
    }

    // Filename from a Content-Disposition header, or null. Prefers filename*=UTF-8''<encoded>
    // (non-ASCII names), then a quoted filename="..." (which may contain ; or escaped quotes),
    // then a bare filename=token.
    public static getFileNameFromContentDisposition(header: string | null): string | null {
        if (!header) {
            return null;
        }
        let extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
        if (extended) {
            try {
                return decodeURIComponent(extended[1].trim());
            } catch (e) {
                // Malformed encoding - fall back to the plain filename
            }
        }
        let quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(header);
        if (quoted) {
            return quoted[1].replace(/\\(.)/g, "$1");
        }
        let token = /filename\s*=\s*([^;\s]+)/i.exec(header);
        return token ? token[1] : null;
    }

    // useServerFileName: prefer the filename in the response's Content-Disposition header,
    // falling back to fileName - for files whose name and type are set where they're stored.
    public static async downloadCSV(route:string, fileName:string, body:any, useServerFileName:boolean = false):Promise<boolean>{
        // Resolves false rather than rejecting on failure - callers await this without a
        // catch, and a promise that never settles leaves their loading overlay up forever.
        return new Promise((resolve) =>{
            let xhttp = new XMLHttpRequest();
            xhttp.onreadystatechange = () => {
                if (xhttp.readyState !== 4) {
                    return;
                }
                if (xhttp.status !== 200) {
                    console.error(`Download failed for ${route} (${xhttp.status})`);
                    return resolve(false);
                }
                // make downloadable link
                let a = document.createElement('a')
                a.href = window.URL.createObjectURL(xhttp.response)
                let serverFileName = useServerFileName
                    ? NetworkUtil.getFileNameFromContentDisposition(xhttp.getResponseHeader("Content-Disposition"))
                    : null;
                a.download = serverFileName || fileName; // give the download a filename
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                resolve(true);
            };
            xhttp.onerror = () => {
                console.error(`Network error downloading ${route}`);
                resolve(false);
            };
            xhttp.open("POST",  route)
            if(NetworkUtil.UserContext){
                xhttp.setRequestHeader("Authorization", `Bearer ${NetworkUtil.UserContext.accessToken}`)
            }
            xhttp.setRequestHeader("Content-Type", "application/json");
            xhttp.responseType = 'blob';
            xhttp.send(JSON.stringify(body));
        })
    }

    public static buildURI(route: string): string {
        if(route[0] !== "/") {
            route = "/" + route;
        }
        return domain + route;
    }

    // Like makePostForBuffer, but also returns response headers (e.g. X-Skipped-ReqNums on bulk downloads)
    public static async makePostForBufferWithHeaders(route:string, body:any): Promise<{blob?: Blob, headers?: Headers, error?: ServerResponse}> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ?
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}`
            } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });

        if(!res.ok){
            try {
                return { error: { ...(await res.json()), success: false } as ServerResponse };
            } catch (e) {
                return { error: { success: false, reason: `Request failed (${res.status})` } as ServerResponse };
            }
        }
        return { blob: await res.blob(), headers: res.headers };
    }

    public static async makePostForBuffer<T>(route:string, body:any): Promise<Blob | ServerResponse> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ?
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}` 
            } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });

        // Any non-2xx is a JSON error body; returning it as a Blob made callers open it as a broken PDF
        if(!res.ok){
            try {
                // force success:false so callers' checks work even if the error body omits it
                return { ...(await res.json()), success: false } as ServerResponse;
            } catch (e) {
                return { success: false, reason: `Request failed (${res.status})` } as ServerResponse;
            }
        }
        return res.blob();
    }

    public static async makeGet<T>(route:string):Promise<T>{
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ?
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}` } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        let res = await fetch(NetworkUtil.buildURI(route),{
                method: 'GET',
                headers
            })

        let json = await res.json();
        return json as T;
    }
    
    public static async makePost<T>(route: string, body: any): Promise<T> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ? 
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization' : `Bearer ${NetworkUtil.UserContext.accessToken}` } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });
        let json = await res.json();
        return json as T;
    }

    //used to get Insurance Images for Samples Modal
    public static async makePostForBlob<T>(route:string, body:any): Promise<Blob | ServerResponse> {
        let headers = NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken ? 
            { 
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization' : `Bearer ${NetworkUtil.UserContext.accessToken}` } :
            {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        let res = await fetch(NetworkUtil.buildURI(route), {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });


        if(res.status === 404 || res.status === 500){
            let json = await res.json();
            return json as ServerResponse;
        }
        else{
            let blob = await res.blob();
            blob = URL.createObjectURL(blob) as any
            return blob as Blob;
        }
    }

    /**
     * Does not stringify the body or sent application/json headers.
     * Should only be used when posting FORM data, not data serialized as JSON.
     */
    public static async postForm<T>(route: string, body: any): Promise<T> {
        let res;
        if(NetworkUtil.UserContext && NetworkUtil.UserContext.accessToken){
            res = await fetch(NetworkUtil.buildURI(route),{
                method: 'POST',
                headers: {'Authorization': `Bearer ${NetworkUtil.UserContext.accessToken}`},
                body: body
            })
        } else {
            res = await fetch(NetworkUtil.buildURI(route), {
                method: 'POST',
                body: body
            });
        } 
        let json = await res.json();
        return json as T;
    }

    // render nothing on screen
    render() {
        return (
            <>
            </>
        )
    }
}
